import React from 'react'
import { Link, useParams } from 'react-router-dom'
import EventBreadcrumb from './EventBreadcrumb'
import EventSection from './EventSection'
import EventSectionNav from './EventSectionNav'
import EventSidebar from './EventSidebar'
import EventSkeleton from './EventSkeleton'
import useLoad from '../functions/useLoad'
import { useHashScroll } from '../functions/useHashScroll'
import { loadEvent, loadExhibitors, loadWpPages } from '../apis/publicApi'
import joinWpTiles from '../functions/joinWpTiles'
import eventTrail from '../functions/eventTrail'
import { LABELS } from '../constants/staticText'
import { useDocumentTitle } from '../../lib/a11y/useDocumentTitle'
import { useFocusHeading } from '../../lib/a11y/useFocusHeading'

// Ein Messe-Abschnitt steht im selben Zweispalter wie die Messeseite (Spec-
// Aenderung 2026-09-30, vorher volle Breite #82 und Tab-Leiste #111): links
// die gekuerzte Sidebar, darunter der Rueckweg zur Messe und die Bereiche als
// Liste, rechts der Abschnitt. Die Hauptnavigation der Website kommt aus dem
// Theme und bleibt unberuehrt.
//
// Die WP-Seiten braucht nur die Liste — der Abruf laeuft nebenher und
// haelt die Seite nicht auf (Bereitschaft wie seit #82 nur an Event und
// Ausstellern). Bis zur Antwort fehlen die WP-Eintraege schlicht; ein Fehler
// unterdrueckt sie nur, denn die Liste ist Navigation, kein Inhalt.
const EventSectionView = () => {
    const { eventSlug, sectionSlug } = useParams()
    const event = useLoad(() => loadEvent(eventSlug), [eventSlug])
    const exhibitors = useLoad(() => loadExhibitors(eventSlug), [eventSlug])
    const wpPages = useLoad(loadWpPages, [])

    const laedt = event.loading || exhibitors.loading

    // Eine wpPage-Kachel ist keine Route (Audit 2026-10-07, S4): sie verweist
    // auf ihre WordPress-Seite. Mit Slug aufgerufen gilt sie als unbekannt —
    // sonst stuende hier ein Rahmen ohne Inhalt. SeoContext.php sieht es genauso.
    const kachel = event.data
        ? (event.data.tiles || []).find(t => t.slug === sectionSlug && 'wpPage' !== t.type) || null
        : null
    // Titel und Fokus je Abschnitt (#178): der Screenreader hoert den Wechsel.
    useDocumentTitle(event.data ? (kachel ? `${kachel.title} – ${event.data.name}` : event.data.name) : '')
    // Ohne Scrollen: schmal steht die Sidebar ueber dem Abschnitt, die
    // Ueberschrift liegt unter der Falz. Die Seite beginnt oben wie jede
    // aufgerufene Seite (#82); der Screenreader hoert den Wechsel trotzdem.
    const headingRef = useFocusHeading(!laedt && !!kachel, { preventScroll: true })

    // Verweise von der Anzeigen-Detailseite tragen einen Anker
    // (#aussteller-<id>, #beruf-<id>). Er kann erst greifen, wenn die Listen
    // stehen — vorher gibt es das Ziel im Dokument nicht (#53).
    useHashScroll(!laedt)

    if (laedt) return <EventSkeleton variant="section" />
    if (event.error || !event.data) return <p className="event-state" role="status">{LABELS.error}</p>

    const tile = kachel

    return (
        <div className="event-section-view">
            <EventBreadcrumb trail={eventTrail(event.data, tile)} />
            <div className="event-columns">
                <div className="event-section-aside">
                    <EventSidebar event={event.data} compact />
                    <Link className="event-back" to={`/events/${event.data.slug}/`}>{LABELS.backToEvent}</Link>
                    <EventSectionNav
                        event={event.data}
                        tiles={joinWpTiles(event.data.tiles || [], wpPages.data || [])}
                        activeSlug={sectionSlug}
                    />
                </div>
                <div className="event-main">
                    <EventSection tile={tile} event={event.data} data={exhibitors.data} headingRef={headingRef} />
                </div>
            </div>
        </div>
    )
}

export default EventSectionView
